import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import { Button,Checkbox,FormControl,FormControlLabel,FormGroup,IconButton,InputLabel,MenuItem,Paper,Select,Skeleton,Slider,Stack,TextField,Typography } from '@mui/material';
import { useMask } from '@react-input/mask';
import { useMutation,useQuery } from '@tanstack/react-query';
import React,{ useContext,useRef,useState } from 'react';
import type { PasswordRules,UserRegisterForm } from '../../types/users/types';
import AvatarEditor from 'react-avatar-editor';
import { useTranslation } from 'react-i18next';
import { AiOutlineEye,AiOutlineEyeInvisible } from 'react-icons/ai';
import { RiErrorWarningFill } from 'react-icons/ri';
import { toast } from 'react-toastify';
import ProfilePic from "../../assets/images/profile.png";
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Companies from '../../service/Companies';
import queryClient from '../../service/query';
import Users from '../../service/Users';

export type { UserRegisterForm as user } from '../../types/users/types';

const initialUser: UserRegisterForm = {
  id: '',
  nome: '',
  email: '',
  contato: '',
  status: 'ativo',
  perfil: '',
  matricula: '',
  senha: '',
  aprovador: false,
  empresa: '',
  gerente: '',
  notificacao: '1',
  passwdReset: false,
  foto: '',
  sendEmail: false,
  gestorPA: false,
  inspetorPA: false,
};

const companySrvc = new Companies();


const userService = new Users();

function RegrasSenha({ regra, label, valor }: { regra: boolean, label: string, valor: number }) {
  return (
    regra && valor > 0 && <Typography fontSize='small' color='textDisabled' fontWeight={600}>{label}</Typography>
  );
}

export default function UserRegister() {
  const { userData } = useContext(UserContext);
  const avatarEditorRef = useRef<AvatarEditor | null>(null);
  const [file, setFile] = useState<File | string>();
  const [user, setUser] = useState<UserRegisterForm>(initialUser);
  const [validPassword, setValidPassword] = useState<string>('');
  const [inputType, setInputType] = useState("password");
  const [scale, setScale] = useState<number>(1);
  const { t } = useTranslation('translation');
  const inputRef = useMask({ mask: '(__) ____-_____', replacement: { _: /\d/ } });

  const profileMocks = [
    { label: t('profiles.manager'), value: 'Gerente' },
    { label: t('profiles.coordinator'), value: 'Gestor' },
    { label: 'Supervisor', value: 'Supervisor' },
    { label: t('profiles.user'), value: 'Usuario' }
  ];

  function checkChar(value: string): undefined | string {
    if (value === '') {
      return '';
    }
    const pattern = /^[A-Za-z0-9-]+$/;
    if (pattern.test(value)) {
      return value;
    }
    return;
  }

  const handleEmail = (value: string): undefined | string => {
    if (value === '') {
      return '';
    }
    const pattern = /^[a-zA-Z0-9@._-]*$/;
    if (pattern.test(value)) {
      return value;
    }
    return;
  };

  const { data, isLoading, isFetching } = useQuery<PasswordRules | undefined>({
    queryKey: ['GET_REGRAS'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const body = { ...token, empresa: token.userEmpresa };
        const response: PasswordRules | undefined = await companySrvc.getAllPoliticaSenha(body);
        return response;
      }
    },
    throwOnError: (error: unknown) => {
      toast.error(t('gestaoUsuarios.erroBuscaRegra'), { toastId: 'fetch-password-rules-error' });
      return false;
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 1000 * 60 * 5,
  });

  function checkPasswordRules(value: PasswordRules | undefined, senha: string) {
    if (!value || !senha) {
      return {
        digit: false,
        lowercase: false,
        uppercase: false,
        special: false,
        length: false,
      };
    }

    const rules = {
      digit: value.digit > 0 ? new RegExp(`([0-9]{${value.digit},})`) : null,
      lowercase: value.lowercase > 0 ? new RegExp(`([a-z]{${value.lowercase},})`) : null,
      uppercase: value.uppercase > 0 ? new RegExp(`([A-Z]{${value.uppercase},})`) : null,
      special: value.special > 0 ? new RegExp(`[!@#$%^&*(),.?":{}|<>]{${value.special},}`) : null,
      length: value.min > 0 ? new RegExp(`(.{${value.min},})`) : null,
    };

    return {
      digit: !rules.digit || rules.digit.test(senha),
      lowercase: !rules.lowercase || rules.lowercase.test(senha),
      uppercase: !rules.uppercase || rules.uppercase.test(senha),
      special: !rules.special || rules.special.test(senha),
      length: !rules.length || rules.length.test(senha),
    };
  }

  const { mutate: handleSubmit, isPending } = useMutation({
    mutationKey: ['CREATE_USER'],
    mutationFn: async () => {
      if (validPassword !== user?.senha) return toast.warning(`${t('users.userRegister.passwordWarning')}`);
      const token = await AttToken();
      if (token) {
        toast.info(t('gestaoUsuarios.cadastraUser'), { toastId: 'creating-user' });
        const result = await userService.registerUser({
          ...token,
          user: {
            ...user,
            gerente: token.username,
            empresa: token.userEmpresa,
            foto: file
          }
        });
        return result;
      }
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      trackEventMatomo('cadastro de usuário', 'erro', 'salvamento', 'falha ao cadastrar usuário');
      toast.error(error?.response?.data?.message || t('gestaoUsuarios.erroCadastro'), { toastId: 'create-user-error' });
    },
    onSuccess: () => {
      trackEventMatomo('cadastro de usuário', 'sucesso', 'salvamento', 'usuário cadastrado');
      toast.success(`${t('users.userRegister.registeredMessage')}`);
      setUser(initialUser);
      setValidPassword('');
      queryClient.resetQueries({ queryKey: ['GET_USERS'] });
      setFile(ProfilePic);
    }
  });

  const onLoading = isPending || isLoading || isFetching;

  const toggleEye = () => {
    inputType === "password" ? setInputType("text") : setInputType("password");
  };

  const handleChange = (newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setScale(newValue);
    }
  };

  function getBase64(file: File, cb: (s: string) => void) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      cb(reader.result as string);
    };
    reader.onerror = function (error) {
      console.error('Error: ', error);
    };
  }

  const regexUser = /^[A-Za-z0-9_-]+$/;

  const regexEmail = /^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

  function validatePassword() {
    if (user?.senha !== validPassword) {
      return <Typography color='textDisabled'>{t('users.userRegister.passwordWarning')} <RiErrorWarningFill /></Typography>;
    }
  };

  function validateEmail() {
    if (user?.email?.length && !regexEmail.test(user?.email ?? '')) {
      return <Typography color='textDisabled'>{t('emailInvalid')} <RiErrorWarningFill /></Typography>;
    }
  };

  function disableButton() {
    if (!data) return true;
    const rules = checkPasswordRules(data, user.senha);
    if (
      (user?.senha !== validPassword) ||
      (user?.senha === '') ||
      (!regexUser.test(String(user?.id ?? ''))) ||
      (!regexEmail.test(user?.email ?? '')) ||
      (user?.perfil === '') ||
      !Object.values(rules).every(Boolean) ||
      (user?.contato?.length < 14)
    ) {
      return true;
    }

    return false;
  }

  function handleEdit<Key extends keyof UserRegisterForm>(
    campo: Key,
    valor: UserRegisterForm[Key],
  ) {
    setUser((prev) => ({ ...prev, [campo]: valor }));
  }

  return (
    <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' width='100%' spacing={1}>
      <Paper sx={{ width: '100%', padding: 3 }}>
        <Stack direction='row' justifyContent='flex-start' alignItems='flex-start' spacing={2} width='100%'>
          <Stack direction='column' justifyContent='flex-start' alignItems='center' spacing={2}>
            <Typography color='textDisabled'>{t('users.userRegister.importImage')}</Typography>
            <Button
              disabled={isPending}
              size='small'
              startIcon={<CloudUploadIcon />}
              style={{
                width: 'fit-content',
                backgroundColor: '#643E91',
                color: '#fff',
                marginBottom: '10px',
              }}
            >
              {'Upload Imagem'}
              <>
                <input
                  disabled={isPending}
                  className='file-input'
                  value=''
                  type="file"
                  accept="image/png, image/jpeg"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    trackEventMatomo('cadastro de usuário', 'click', 'input', 'add imagem');
                    e.target.files && getBase64(e.target.files[0], (base64) => {
                      setFile(base64);
                    });
                  }}
                />
              </>
            </Button>
            <AvatarEditor
              image={!file ? ProfilePic : file}
              width={200}
              height={200}
              border={10}
              borderRadius={200}
              color={[243, 243, 244, 0.6]}
              scale={scale}
              rotate={0}
              ref={avatarEditorRef}
            />
            <Typography color='textDisabled'>Zoom:</Typography>
            <Slider
              disabled={isPending}
              aria-label="Always visible"
              defaultValue={100}
              step={0.1}
              min={1}
              max={5}
              value={scale}
              style={{ width: '30%', color: '#47aa8d' }}
              onChange={(eve, value) => handleChange(value)}
            />
          </Stack>
          <Stack
            width='100%'
            direction='row'
            spacing={1}
            alignItems='flex-start'
            component='form'
            autoComplete='new-password'
            onSubmit={() => handleSubmit()}
            onInvalid={() => t('gestaoUsuarios.preencha')}>
            <Stack width='100%' direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1.5}>
              <TextField
                disabled={isPending}
                size='small'
                fullWidth
                onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
                required
                label={t('name')}
                value={user?.nome}
                onChange={(e) => handleEdit('nome', e.target.value)}
              />
              <TextField
                disabled={isPending}
                size='small'
                fullWidth
                onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
                required
                label={t('email')}
                value={user?.email}
                onChange={(e) => {
                  const userEmail = handleEmail(e.target.value);
                  if (userEmail === undefined) return;
                  handleEdit('email', userEmail);
                }}
              />
              <TextField
                disabled={isPending}
                size='small'
                fullWidth
                onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
                required
                value={user?.id ?? ''}
                label={t('loginScreen.username')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const userName = checkChar(e.target.value);
                  if (userName === undefined) return;
                  handleEdit('id', userName);
                }}
              />
              <Stack
                width='100%'
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <TextField
                  disabled={isPending}
                  size='small'
                  fullWidth
                  onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
                  required
                  type={inputType}
                  value={user?.senha}
                  label={t('loginScreen.password')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const senha = e.target.value.trim();
                    handleEdit('senha', senha);
                    data && checkPasswordRules(data, senha);
                  }}
                />
                <IconButton onClick={toggleEye} color='info'>
                  {inputType === "password"
                    ? <AiOutlineEye />
                    : <AiOutlineEyeInvisible />}
                </IconButton>
              </Stack>
              {validatePassword()}
              {validateEmail()}
            </Stack>
            <Stack width='100%' direction="column" justifyContent="flex-start" alignItems="flex-end" spacing={1.5}>
              <FormControl fullWidth>
                <InputLabel>{t('profile')}</InputLabel>
                <Select
                  disabled={isPending}
                  size='small'
                  value={user?.perfil}
                  onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'select', 'seleciona valor')}
                  onChange={(event) => handleEdit('perfil', event.target.value)}
                  label={t('profile')}
                >
                  {profileMocks?.map((option: { value: string, label: string }) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                disabled={isPending}
                size='small'
                fullWidth
                onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
                required
                value={user?.contato}
                onChange={(even) => handleEdit('contato', even.target.value)}
                label={t('tel')}
                inputRef={inputRef}
              />
              <TextField
                disabled={isPending}
                size='small'
                fullWidth
                onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
                required
                label={t('registration')}
                value={user?.matricula}
                onChange={(e) => handleEdit('matricula', e.target.value)}
              />
              <TextField
                disabled={isPending}
                size='small'
                fullWidth
                onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'input', 'edita valor')}
                autoComplete='new-password'
                required
                value={validPassword ?? ''}
                type={inputType}
                label={t('users.userRegister.confirmation')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidPassword(e.target.value.trim())}
              />
              <FormGroup>
                <FormControlLabel
                  labelPlacement='start'
                  disabled={isPending}
                  control={<Checkbox
                    onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'checkbox', 'seleciona valor')}
                    checked={user?.sendEmail}
                    onChange={(eve) => handleEdit('sendEmail', eve.target.checked)} />} label={t('users.userRegister.sendPassword')} />
                <FormControlLabel
                  labelPlacement='start'
                  disabled={isPending}
                  control={<Checkbox
                    disabled={isPending}
                    onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'checkbox', 'seleciona valor')}
                    checked={user?.aprovador}
                    onChange={(eve) => handleEdit('aprovador', eve.target.checked)} />} label={t('approver')} />
                {userData?.concedeAcesso
                  && <>
                    <FormControlLabel
                      labelPlacement='start'
                      disabled={isPending}
                      control={<Checkbox
                        disabled={isPending}
                        onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'checkbox', 'seleciona valor')}
                        checked={user?.gestorPA}
                        onChange={(eve) => handleEdit('gestorPA', eve.target.checked)} />} label={t('users.userRegister.actionPlanManager')} />
                    <FormControlLabel
                      labelPlacement='start'
                      disabled={isPending}
                      control={<Checkbox
                        disabled={isPending}
                        onClick={() => trackEventMatomo('cadastro de usuário', 'click', 'checkbox', 'seleciona valor')}
                        checked={user?.inspetorPA}
                        onChange={(eve) => handleEdit('inspetorPA', eve.target.checked)} />} label={t('users.userRegister.actionPlan')} />
                  </>}
              </FormGroup>
              <Button
                onClick={() => {
                  handleSubmit();
                  trackEventMatomo('cadastro de usuário', 'click', 'button', 'salva');
                }}
                endIcon={<SaveIcon />}
                disabled={disableButton() || isPending}
                variant='contained'
                color='success'
              >
                {t('save')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
      {onLoading
        ? <Skeleton variant='text' width={250} height={35} />
        : (() => {
          if (!data) return null;
          const rules = checkPasswordRules(data, user.senha);
          return (
            <>
              <Typography fontSize='medium' fontWeight={600} color='textDisabled'>{t('users.userRegister.passwordRules')}</Typography>
              <RegrasSenha valor={data.digit} label={`*${t('settingsPage.password.minNmbr')} ${data.digit}`} regra={!rules.digit} />
              <RegrasSenha valor={data.lowercase} label={`*${t('settingsPage.password.minLower')} ${data.lowercase}`} regra={!rules.lowercase} />
              <RegrasSenha valor={data.uppercase} label={`*${t('settingsPage.password.minUpper')} ${data.uppercase}`} regra={!rules.uppercase} />
              <RegrasSenha valor={data.special} label={`*${t('settingsPage.password.minSpecial')} ${data.special}`} regra={!rules.special} />
              <RegrasSenha valor={data.min} label={`*${t('settingsPage.password.minChar')} ${data.min}`} regra={!rules.length} />
              {user?.senha && user?.senha?.length > 0 && Object.values(rules).every(Boolean) && <Typography color='textDisabled'>{t('gestaoUsuarios.senhaAtende')}</Typography>}
            </>
          );
        })()}
    </Stack>
  );
}
